import { Breadcrumbs, Loader, Skeleton, Tabs } from '@mantine/core';
import { IconCloudPlus, IconCurrencyBaht, IconRefresh } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { AccessBadge, Button } from '../../../../components/Atoms';
import { useActiveProject, useProductAccess } from '../../../../hooks/useActiveProject';
import useBreadcrumbItems from '../../../../hooks/useBreadcrumbItems';
import usePageHeading from '../../../../hooks/usePageHeading';
import { useQuotasQuery } from '../../../../hooks/useQuotasQuery';
import { useInstanceListQuery } from './../../../../hooks/CloudComputeHooks/useInstanceListQuery';
import InstanceListTab from './components/InstanceListTab/InstanceListTab';
import SecurityGroupListTab from './components/SecurityGroupListTab/SecurityGroupListTab';

export default function CloudComputePage() {
    const { t } = useTranslation('cloudCompute');
    const headingRef = usePageHeading(t('page.title'));
    const [isRefreshing, setIsRefreshing] = useState(false);
    const {
        data: instanceData,
        isPending: isInstancePending,
        collective: instanceCollectiveData,
        refetch: refetchInstances,
    } = useInstanceListQuery({ refetchInterval: 3500 });
    const { instances: instanceQuota } = useQuotasQuery();
    const vmAccess = useProductAccess('vm');
    const { project } = useActiveProject();
    const quotaOwner = project && project.role !== 'owner' ? project.owner.name : '';

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.product'), href: '#' },
        { title: t('breadcrumb.cloudCompute'), href: '/cloud-compute' },
    ]);

    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const activeTab = tabParam === 'security-group' ? 'security-group' : 'instance';

    const handleTabChange = (next: string | null) => {
        if (!next) return;
        const params = new URLSearchParams(searchParams);
        if (next === 'instance') params.delete('tab');
        else params.set('tab', next);
        setSearchParams(params, { replace: true });
    };

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin={'xs'} className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full flex flex-col gap-2 justify-between lg:flex-row lg:gap-0">
                    <div className="flex flex-col justify-start items-start gap-1">
                        <div className="flex flex-row flex-wrap items-center gap-2">
                            <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                                {t('page.title')}
                            </h1>
                            {vmAccess && vmAccess !== 'manage' && <AccessBadge level={vmAccess} />}
                        </div>
                        <p className="w-auto max-w-135 text-base text-fg/68">
                            {t('page.description')}
                        </p>
                    </div>
                    <div className="flex flex-row flex-wrap justify-start items-end gap-2 sm:justify-end">
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconRefresh stroke={1.5} />}
                            onClick={() => {
                                setIsRefreshing(true);
                                void refetchInstances().finally(() => {
                                    setIsRefreshing(false);
                                });
                            }}
                            isLoading={isRefreshing}
                        >
                            {t('buttons.refresh')}
                        </Button>
                        {vmAccess === 'manage' && (
                            <Button
                                href="/cloud-compute/deployment"
                                size="sm"
                                leftIcon={<IconCloudPlus stroke={1.5} aria-hidden="true" />}
                                className="w-full xl:w-auto"
                            >
                                {t('buttons.deployment')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            <div className="w-full grid grid-cols-1 lg:grid-cols-4 border-[0.5px] border-line rounded-sm">
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 border-b-[0.5px] border-line lg:border-b-0 bg-surface-1 rounded-none lg:rounded-l-sm">
                    <h2 className="text-xs text-fg/68 sm:text-base">
                        {t('stats.activeInstances')}
                    </h2>
                    {instanceCollectiveData && !isInstancePending && instanceQuota ? (
                        <>
                            <span className="text-xl font-bold sm:text-2xl">
                                {instanceCollectiveData.instances}
                            </span>
                            <span className="text-xs text-fg/68 sm:text-sm">
                                {quotaOwner
                                    ? t('stats.quotaOwner', {
                                          current: instanceQuota.current,
                                          max: instanceQuota.limit,
                                          owner: quotaOwner,
                                      })
                                    : t('stats.quotaOwn', {
                                          current: instanceQuota.current,
                                          max: instanceQuota.limit,
                                      })}
                            </span>
                        </>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 bg-surface-1 border-b-[0.5px] border-l-0 lg:border-l-[0.5px] lg:border-b-0 border-line">
                    <h2 className="text-xs text-fg/68 sm:text-base">{t('stats.vcpuInUse')}</h2>
                    {instanceCollectiveData && !isInstancePending ? (
                        <div className="inline-flex items-center gap-1">
                            <span className="text-xl font-bold sm:text-2xl">
                                {instanceCollectiveData.vcpus}
                            </span>
                            <span className="text-xs text-fg/68 sm:text-base">
                                {t('stats.vcpuSuffix')}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 bg-surface-1 border-b-[0.5px] border-l-0 lg:border-l-[0.5px] lg:border-b-0 border-line">
                    <h2 className="text-xs text-fg/68 sm:text-base">{t('stats.memoryInUse')}</h2>
                    {instanceCollectiveData && !isInstancePending ? (
                        <div className="inline-flex items-center gap-1">
                            <span className="text-xl font-bold sm:text-2xl">
                                {instanceCollectiveData.memory / 1024}
                            </span>
                            <span className="text-xs text-fg/68 sm:text-base">
                                {t('stats.memorySuffix')}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 bg-surface-1 border-l-0 lg:border-l-[0.5px] border-line rounded-r-sm">
                    <h2 className="text-xs text-fg/68 sm:text-base">{t('stats.hourlyCharges')}</h2>
                    {instanceCollectiveData && !isInstancePending ? (
                        <div className="inline-flex items-center gap-1">
                            <div className="inline-flex items-center text-xl font-bold sm:text-2xl">
                                {instanceCollectiveData.hourly.toFixed(2)}
                                <IconCurrencyBaht stroke={2.5} />
                            </div>
                            <span className="hidden text-fg/68 sm:inline sm:text-base text-nowrap">
                                {t('stats.hourlySuffix')}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
                <div className="w-full h-auto bg-surface-1 border-l-[0.5px] border-line"></div>
                <div className="w-full h-auto bg-surface-1 border-l-[0.5px] border-line rounded-r-sm"></div>
            </div>
            <Tabs
                className="w-full space-y-4"
                value={activeTab}
                onChange={handleTabChange}
                color="var(--fg)"
                keepMounted={false}
            >
                <Tabs.List aria-label={t('tabs.label')} className="tabs-list-thin">
                    <Tabs.Tab
                        value={'instance'}
                        rightSection={
                            <div className="w-5 h-5 flex justify-center items-center font-bold text-xs rounded-sm bg-surface-3">
                                {instanceCollectiveData && !isInstancePending ? (
                                    instanceCollectiveData.instances
                                ) : (
                                    <Loader size={8} color="var(--fg)" type="bars" />
                                )}
                            </div>
                        }
                    >
                        {t('tabs.instances')}
                    </Tabs.Tab>
                    <Tabs.Tab value={'security-group'}>{t('tabs.securityGroups')}</Tabs.Tab>
                </Tabs.List>

                <InstanceListTab instances={instanceData} isLoading={isInstancePending} />
                <SecurityGroupListTab />
            </Tabs>
        </div>
    );
}
